import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { SEGMENT, SEGMENTS, segmentLabel } from '../brand';
import { evidenceColumns } from '../keys';

/**
 * Block 04 — the queries themselves, on one side of the rule at a time.
 *
 * This is where a definition is checked rather than trusted. The two blocks above can only say
 * how much traffic each side has; this one says which queries made it up and, under a literal
 * rule, which term put each of them there — which is how someone discovers that a short brand
 * term is also a common word, or that a product name is catching a competitor's.
 *
 * Rows whose text disagrees with the side Search Console returned them on are flagged rather than
 * reconciled. Our substring check and Google's RE2 are different implementations of the same
 * rule, and Google's is the one that produced every number on this page, so a disagreement is
 * shown to the user to act on and never used to move a row.
 *
 * One side per request. Switching sides is a new ask of Search Console rather than a filter over
 * rows already here — the excluded queries were never fetched, and a local filter over the
 * matched ones could not produce them.
 *
 * What comes back is evidence, not an inventory. Search Console withholds queries below its own
 * thresholds, so a short list means a short list was returned; it never means the side is small,
 * and it certainly never means it is empty.
 */
class QueryEvidence extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        // DataTable calls this rather than rendering it, so it is bound here instead of being
        // handed over as an element.
        this.additionalBox = this.additionalBox.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    /** The side picker, as the table filter control every other report uses. */
    additionalBox() {
        const { t, segment, onSegment, status } = this.props;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {SEGMENTS.map((value) => (
                            <button
                                key={value}
                                type="button"
                                className={segment === value ? 'active' : ''}
                                disabled={status === 'loading'}
                                onClick={() => onSegment(value)}
                            >
                                {t('tools.brand_non_brand.evidence.side', {
                                    segment: segmentLabel(t, value),
                                })}
                            </button>
                        ))}
                    </div>
                </div>

                <span className="gsc-tool-field-note">
                    {t('tools.brand_non_brand.evidence.side_note')}
                </span>
            </div>
        );
    }

    render() {
        const { t, scope, segment, status, error, rows, period, shortened, reasonFor } = this.props;

        const columns = evidenceColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            reasonFor,
        });

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">{t('tools.brand_non_brand.evidence.title')}</p>
                        <p className="gsc-tool-sub">
                            {t('tools.brand_non_brand.evidence.description')} ·{' '}
                            {formatPeriod(period)}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {/* Said before the rows, not under them: a reader comparing this block with
                        the totals above has to know the windows differ before they conclude the
                        numbers disagree. */}
                    {shortened && (
                        <div className="gsc-tool-banner">
                            {t('tools.brand_non_brand.evidence.shortened', {
                                period: formatPeriod(period),
                            })}
                        </div>
                    )}

                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            <DataTable
                                nodes={rows}
                                COLUMNS={columns}
                                sortFns={getSortKeys(columns)}
                                additionalStyles={setAdditionalStyles(columns)}
                                searchFunction={this.searchFunction}
                                additionalBox={this.additionalBox}
                                fileName={t('tools.brand_non_brand.evidence.file', {
                                    segment: segmentLabel(t, segment),
                                })}
                                customEmptyMessage={t(
                                    segment === SEGMENT.BRAND
                                        ? 'tools.brand_non_brand.evidence.empty_brand'
                                        : 'tools.brand_non_brand.evidence.empty_non_brand',
                                )}
                                customContainerStyles="gsc-tool-key-table"
                            />

                            <p className="gsc-tool-table-note">
                                {t('tools.brand_non_brand.evidence.sample_note')}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.brand_non_brand.evidence.no_intent_note')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(QueryEvidence);
